import React from "react"
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Divider,
  useTheme,
  IconButton,
  CloseIcon,
  Button,
  UnorderedList,
  UnorderedListItem,
  ListItemText,
  Link,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DownIcon
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl, FormattedDate } from "react-intl"
import moment from "moment/moment"
import { useFlags } from "launchdarkly-react-client-sdk"
import { ExpenseState } from "#/utils/ExpenseState"
import { ExpenseType } from "#/utils/ExpenseType"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"
import { Reimbursement } from "#/types/reimbursement"
import { getFileOnClick } from "#/components/views/reimbursements/upload/Attachments"
import { useInvoices } from "#/hooks/useInvoices"
import type { Invoice } from "#/hooks/useInvoices"
import {
  CardHistoryEntries,
  DirectPayHistoryEntries,
  OopHistoryEntries
} from "#/components/views/reimbursements/TransactionsDrawerHistoryEntries"

interface TransactionDetailsDrawerProps {
  open: boolean
  onClose: () => void
  transaction: Reimbursement
}

export const TransactionDetailsDrawer: React.FC<TransactionDetailsDrawerProps> = ({ open, onClose, transaction }) => {
  const intl = useIntl()
  const theme = useTheme()
  const { transactionDetailsDrawerForOopAndCardEnabled } = useFlags()

  const invoiceExpenseId: number | null = (() => {
    if (!transaction) return null
    if (!transaction.hasBeenReconciled) return null
    return transaction.reconciliationRequestedExpenseId || transaction.expenseId
  })()

  const invoiceQueryResults = useInvoices(invoiceExpenseId ? [invoiceExpenseId] : [])
  const invoices: Invoice[] = invoiceQueryResults[0]?.data ?? []
  const openInvoice = invoices.find((inv) => inv.status === "open") ?? null

  if (!transaction) {
    return null
  }

  // Check expense type
  const isDirectPayExpense = transaction.expenseType === ExpenseType.DIRECT_PAY
  const isOopExpense = transaction.expenseType === ExpenseType.OOP
  const isCardExpense = transaction.expenseType === ExpenseType.CARD

  const showReconciliationAlert =
    openInvoice !== null &&
    ((isCardExpense && transaction.expenseState === ExpenseState.DENIED_RECONCILIATION_REQUESTED) ||
      (isOopExpense &&
        transaction.expenseState === ExpenseState.PAID &&
        !!transaction.reconciliationRequestedExpenseId))

  // Determine if drawer should be shown
  // DIRECTPAY always shows, OOP/CARD show when feature flag is enabled
  const shouldShowDrawer =
    isDirectPayExpense || (transactionDetailsDrawerForOopAndCardEnabled && (isOopExpense || isCardExpense))

  if (!shouldShowDrawer) {
    return null
  }

  const shouldShowFullContent = isDirectPayExpense
  const shouldShowOopCardContent = isOopExpense || isCardExpense

  // Only calculate these for DIRECTPAY expenses to avoid accessing properties that may not exist for OOP/CARD
  const serviceDateStringToDate = isDirectPayExpense ? moment(transaction.serviceDate).toDate() : null

  const getWhoReceivedCare = () => {
    switch (transaction.whoReceivedCare) {
      case "MEMBER":
        return intl.formatMessage({ defaultMessage: "Me" })
      case "LEGAL_PARTNER":
        return intl.formatMessage({ defaultMessage: "My legal spouse or tax-dependent partner" })
      case "NON_MARRIED_PARTNER":
        return intl.formatMessage({ defaultMessage: "My non-married partner" })
      case "BIRTH_PARENT":
        return intl.formatMessage({ defaultMessage: "Birth parent for adoption" })
      case "DONOR":
        return intl.formatMessage({ defaultMessage: "Donor" })
      case "GESTATIONAL_CARRIER":
        return intl.formatMessage({ defaultMessage: "Gestational carrier" })
      case "PARTNER":
        return intl.formatMessage({ defaultMessage: "Partner" })
      default:
        return transaction.whoReceivedCare
    }
  }

  const getJourneyTypeLabel = () => {
    switch (transaction.journeyType) {
      case JourneyTypes.ADOPTION:
        return intl.formatMessage({ defaultMessage: "Adoption" })
      case JourneyTypes.GC:
        return intl.formatMessage({ defaultMessage: "Gestational carrier" })
      case JourneyTypes.PRESERVATION:
        return intl.formatMessage({ defaultMessage: "Fertility preservation" })
      case JourneyTypes.FERTILITY_CARE:
        return intl.formatMessage({ defaultMessage: "Fertility care" })
      case JourneyTypes.PREGNANCY_AND_POSTPARTUM_SERVICES:
        return intl.formatMessage({ defaultMessage: "Pregnancy and postpartum services" })
      case JourneyTypes.MENOPAUSE:
        return intl.formatMessage({ defaultMessage: "Menopause" })
      case JourneyTypes.LOW_TESTOSTERONE:
        return intl.formatMessage({ defaultMessage: "Low testosterone" })
      case JourneyTypes.GENDER_AFFIRMING_CARE:
        return intl.formatMessage({ defaultMessage: "Gender-affirming care" })
      case JourneyTypes.PATHFINDING:
        return intl.formatMessage({ defaultMessage: "Pathfinding" })
      case JourneyTypes.METABOLIC_FERTILITY_HEALTH:
        return intl.formatMessage({ defaultMessage: "Metabolic fertility health" })
      case JourneyTypes.IVF:
        return intl.formatMessage({ defaultMessage: "IVF" })
      case JourneyTypes.IUI:
        return intl.formatMessage({ defaultMessage: "IUI" })
      default:
        return transaction.journeyType
    }
  }

  const getStorageFeesLabel = () => {
    switch (transaction.relatedToStorageFeesAnswer) {
      case "OTHER":
        return intl.formatMessage({ defaultMessage: "No" })
      case "FIRST_YEAR_STORAGE":
        return intl.formatMessage({ defaultMessage: "Yes: First year of storage" })
      case "POST_FIRST_YEAR_STORAGE":
        return intl.formatMessage({ defaultMessage: "Yes: After first year of storage" })
      default:
        return transaction.relatedToStorageFeesAnswer
    }
  }

  const formatCurrency = (value: number) =>
    intl.formatNumber(value / 100, {
      style: "currency",
      currency: transaction.currencyCode,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 0
    })

  const hasAdjustment =
    isDirectPayExpense &&
    !!transaction.adjustmentDatetime &&
    transaction.originalAmount != null &&
    transaction.originalEligibleAmount != null

  const amountAppliedToDeductibleString = transaction.amountAppliedToDeductible
    ? formatCurrency(transaction.amountAppliedToDeductible)
    : null

  const amountAppliedToCostShareString = transaction.amountAppliedToCostShare
    ? formatCurrency(transaction.amountAppliedToCostShare)
    : null

  const amountOverAvailableFunds =
    transaction.eligibleAmount -
    transaction.amount -
    (transaction.amountAppliedToDeductible ?? 0) -
    transaction.amountAppliedToCostShare

  const amountOverAvailableFundsString = amountOverAvailableFunds ? formatCurrency(amountOverAvailableFunds) : null

  // Original amounts for adjusted transactions
  const originalAmountAppliedToDeductibleString =
    hasAdjustment && transaction.originalAmountAppliedToDeductible
      ? formatCurrency(transaction.originalAmountAppliedToDeductible)
      : null

  const originalAmountAppliedToCostShareString =
    hasAdjustment && transaction.originalAmountAppliedToCostShare
      ? formatCurrency(transaction.originalAmountAppliedToCostShare)
      : null

  const originalAmountOverAvailableFunds = hasAdjustment
    ? transaction.originalEligibleAmount -
      transaction.originalAmount -
      (transaction.originalAmountAppliedToDeductible ?? 0) -
      (transaction.originalAmountAppliedToCostShare ?? 0)
    : 0

  const originalAmountOverAvailableFundsString = originalAmountOverAvailableFunds
    ? formatCurrency(originalAmountOverAvailableFunds)
    : null

  // Compute refund/owe amount for adjusted transactions
  const memberNewPays = hasAdjustment ? transaction.eligibleAmount - transaction.amount : 0
  const memberOriginalPays = hasAdjustment ? transaction.originalEligibleAmount - transaction.originalAmount : 0
  const adjustmentDifference = memberNewPays - memberOriginalPays
  const memberGetsRefund = adjustmentDifference < 0
  const memberOwesMore = adjustmentDifference > 0

  const FileLinks = () => {
    return (
      <>
        {transaction.fileGuids.map((guid, i) => (
          <Box paddingBottom={theme.tokens.spacing.md} key={i}>
            <Link component="button" onClick={getFileOnClick(guid)}>
              {transaction.fileNames[i]}
            </Link>
          </Box>
        ))}
      </>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={(theme) => ({
        "& .MuiDrawer-paper": {
          inlineSize: { xs: "100%", sm: "400px" },
          padding: theme.tokens.spacing.lg,
          display: "flex",
          flexDirection: "column",
          blockSize: "100%"
        }
      })}
    >
      {/* Header - Fixed at top */}
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={theme.tokens.spacing.lg}>
        <Typography variant="h4" component="h2" color="secondary">
          <FormattedMessage defaultMessage="Expense details" />
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label={intl.formatMessage({ defaultMessage: "Close expense details" })}
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            "&:hover": {
              color: theme.palette.text.primary
            }
          })}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {shouldShowOopCardContent && (
          <Stack spacing={theme.tokens.spacing.lg}>
            {/* Merchant - Card expenses only */}
            {isCardExpense && transaction.merchantName && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Merchant" />
                </Typography>
                <Typography variant="body1">{transaction.merchantName}</Typography>
              </Box>
            )}

            {showReconciliationAlert && (
              <Alert severity="warning">
                <FormattedMessage
                  defaultMessage="You owe a reconciliation payment. Please <link>make a payment</link> to unlock your Carrot Card."
                  values={{
                    link: (linkContent) => (
                      <Link color="inherit" href={openInvoice.invoicePaymentUrl} target="_blank">
                        {linkContent}
                      </Link>
                    )
                  }}
                />
              </Alert>
            )}

            {/* History */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="History" />
              </Typography>
              {isOopExpense ? (
                <OopHistoryEntries transaction={transaction} invoices={invoices} />
              ) : (
                <CardHistoryEntries transaction={transaction} invoices={invoices} />
              )}
            </Box>

            {/* Who received care or services? */}
            {transaction.whoReceivedCare && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Who received care or services?" />
                </Typography>
                <Typography variant="body1">{getWhoReceivedCare()}</Typography>
              </Box>
            )}

            {/* What is this expense related to? */}
            {transaction.journeyType && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="What is this expense related to?" />
                </Typography>
                <Typography variant="body1">{getJourneyTypeLabel()}</Typography>
              </Box>
            )}

            {/* Is this related to storage fees? */}
            {transaction.relatedToStorageFeesAnswer && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Is this related to storage fees?" />
                </Typography>
                <Typography variant="body1">{getStorageFeesLabel()}</Typography>
              </Box>
            )}

            {/* Additional information */}
            {transaction.description && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Additional information" />
                </Typography>
                <Typography variant="body1">{transaction.description}</Typography>
              </Box>
            )}
          </Stack>
        )}

        {shouldShowFullContent && (
          <Stack spacing={theme.tokens.spacing.lg}>
            {/* History */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="History" />
              </Typography>
              <DirectPayHistoryEntries transaction={transaction} />
            </Box>

            {/* Amounts */}
            <Box>
              {hasAdjustment ? (
                <>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="secondary"
                    marginBottom={theme.tokens.spacing.xxs}
                  >
                    <FormattedMessage defaultMessage="Updated amounts" />
                  </Typography>
                  <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
                    <FormattedMessage
                      defaultMessage="Total: {eligibleAmount}"
                      values={{ eligibleAmount: formatCurrency(transaction.eligibleAmount) }}
                    />
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xxs}>
                  <FormattedMessage
                    defaultMessage="Cost: {eligibleAmount}"
                    values={{ eligibleAmount: formatCurrency(transaction.eligibleAmount) }}
                  />
                </Typography>
              )}
              <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
                <FormattedMessage
                  defaultMessage="Carrot pays: {amount}"
                  values={{ amount: formatCurrency(transaction.amount) }}
                />
              </Typography>
              <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
                <FormattedMessage
                  defaultMessage="You pay: {memberAmount}"
                  values={{
                    memberAmount: formatCurrency(transaction.eligibleAmount - transaction.amount)
                  }}
                />
              </Typography>
              <Box paddingLeft={theme.tokens.spacing.sm}>
                <UnorderedList disablePadding={true}>
                  {amountAppliedToDeductibleString && (
                    <UnorderedListItem disablePadding={true}>
                      <ListItemText
                        primary={intl.formatMessage(
                          {
                            defaultMessage: "Applied to deductible: {amountAppliedToDeductibleString}"
                          },
                          {
                            amountAppliedToDeductibleString
                          }
                        )}
                      />
                    </UnorderedListItem>
                  )}
                  {amountAppliedToCostShareString && (
                    <UnorderedListItem disablePadding={true}>
                      <ListItemText
                        primary={intl.formatMessage(
                          {
                            defaultMessage: "Applied to cost share: {amountAppliedToCostShareString}"
                          },
                          {
                            amountAppliedToCostShareString
                          }
                        )}
                      />
                    </UnorderedListItem>
                  )}
                  {amountOverAvailableFundsString && (
                    <UnorderedListItem disablePadding={true}>
                      <ListItemText
                        primary={intl.formatMessage(
                          {
                            defaultMessage: "Amount over available funds: {amountOverAvailableFundsString}"
                          },
                          {
                            amountOverAvailableFundsString
                          }
                        )}
                      />
                    </UnorderedListItem>
                  )}
                </UnorderedList>
              </Box>

              {/* Adjustment explanation text */}
              {hasAdjustment && memberGetsRefund && (
                <Typography variant="body1" fontStyle="italic" marginTop={theme.tokens.spacing.sm}>
                  <FormattedMessage
                    defaultMessage="You can expect a refund of {refundAmount} from your provider. Please check your email for more information."
                    values={{ refundAmount: formatCurrency(Math.abs(adjustmentDifference)) }}
                  />
                </Typography>
              )}
              {hasAdjustment && memberOwesMore && (
                <Typography variant="body1" fontStyle="italic" marginTop={theme.tokens.spacing.sm}>
                  <FormattedMessage
                    defaultMessage="Your provider will collect an additional payment of {additionalAmount} from you. Please check your email for more information."
                    values={{ additionalAmount: formatCurrency(adjustmentDifference) }}
                  />
                </Typography>
              )}
            </Box>

            {/* Original amounts (shown only for adjusted transactions) */}
            {hasAdjustment && (
              <Accordion defaultExpanded sx={{ "&::before": { display: "none" } }}>
                <AccordionSummary
                  expandIcon={<DownIcon />}
                  aria-controls="original-amounts-content"
                  id="original-amounts-header"
                >
                  <Typography variant="body1" fontWeight="bold" color="secondary">
                    <FormattedMessage defaultMessage="Original amounts" />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
                      <FormattedMessage
                        defaultMessage="Total: {eligibleAmount}"
                        values={{ eligibleAmount: formatCurrency(transaction.originalEligibleAmount) }}
                      />
                    </Typography>
                    <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
                      <FormattedMessage
                        defaultMessage="Carrot pays: {amount}"
                        values={{ amount: formatCurrency(transaction.originalAmount) }}
                      />
                    </Typography>
                    <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
                      <FormattedMessage
                        defaultMessage="You pay: {memberAmount}"
                        values={{
                          memberAmount: formatCurrency(transaction.originalEligibleAmount - transaction.originalAmount)
                        }}
                      />
                    </Typography>
                    <Box paddingLeft={theme.tokens.spacing.sm}>
                      <UnorderedList disablePadding={true}>
                        {originalAmountAppliedToDeductibleString && (
                          <UnorderedListItem disablePadding={true}>
                            <ListItemText
                              primary={intl.formatMessage(
                                {
                                  defaultMessage: "Applied to deductible: {amountAppliedToDeductibleString}"
                                },
                                {
                                  amountAppliedToDeductibleString: originalAmountAppliedToDeductibleString
                                }
                              )}
                            />
                          </UnorderedListItem>
                        )}
                        {originalAmountAppliedToCostShareString && (
                          <UnorderedListItem disablePadding={true}>
                            <ListItemText
                              primary={intl.formatMessage(
                                {
                                  defaultMessage: "Applied to cost share: {amountAppliedToCostShareString}"
                                },
                                {
                                  amountAppliedToCostShareString: originalAmountAppliedToCostShareString
                                }
                              )}
                            />
                          </UnorderedListItem>
                        )}
                        {originalAmountOverAvailableFundsString && (
                          <UnorderedListItem disablePadding={true}>
                            <ListItemText
                              primary={intl.formatMessage(
                                {
                                  defaultMessage: "Amount over available funds: {amountOverAvailableFundsString}"
                                },
                                {
                                  amountOverAvailableFundsString: originalAmountOverAvailableFundsString
                                }
                              )}
                            />
                          </UnorderedListItem>
                        )}
                      </UnorderedList>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Service Start Date */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Service start date" />
              </Typography>
              <Typography variant="body1">
                <FormattedDate value={serviceDateStringToDate} year="numeric" month="long" day="numeric" />
              </Typography>
            </Box>

            {/* Who Received Care */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Care recipient" />
              </Typography>
              <Typography variant="body1">{getWhoReceivedCare()}</Typography>
            </Box>

            {/* Service */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Service" />
              </Typography>
              <Typography variant="body1">{transaction.directPayServiceName}</Typography>
            </Box>

            {/* Provider */}
            {transaction.providerName && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Provider" />
                </Typography>
                <Typography variant="body1">{transaction.providerName}</Typography>
              </Box>
            )}

            {/* Infertility diagnosis */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Infertility diagnosis" />
              </Typography>
              <Typography variant="body1">
                {transaction.isInfertilityFlag ? (
                  <FormattedMessage defaultMessage="Yes" />
                ) : (
                  <FormattedMessage defaultMessage="No" />
                )}
              </Typography>
            </Box>

            {/* Medical necessity */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Medical necessity" />
              </Typography>
              <Typography variant="body1">
                {transaction.isMedicalNecessityFlag ? (
                  <FormattedMessage defaultMessage="Yes" />
                ) : (
                  <FormattedMessage defaultMessage="No" />
                )}
              </Typography>
            </Box>

            {/* Attachments */}
            {!transaction.isBenefitTransfer && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Documents" />
                </Typography>
                <FileLinks />
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ marginBlockStart: theme.tokens.spacing.lg }}>
        <Divider sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })} />
        <Box display="flex" justifyContent="flex-end">
          <Button
            color="secondary"
            variant="outlined"
            onClick={onClose}
            sx={() => ({
              minInlineSize: "120px"
            })}
          >
            <FormattedMessage defaultMessage="Close" />
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}
